import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import { Home, BookOpen, Settings, LogOut, Sun, Moon, ShoppingBag, Globe, Bell, FileEdit } from 'lucide-react';
import { useState } from 'react';
import DashboardTopBar from './DashboardTopBar';
import { useNotification } from '../../context/NotificationContext';

export default function DashboardLayout({ theme, toggleTheme }) {
  const { userData, currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { unreadCount } = useNotification();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'نظرة عامة', icon: Home },
    { path: '/dashboard/courses', label: 'سوق الدورات', icon: ShoppingBag },
    { path: '/dashboard/assignments', label: 'التكاليف', icon: FileEdit },
    { path: '/dashboard/notifications', label: 'الإشعارات', icon: Bell, badge: unreadCount },
    { path: '/dashboard/settings', label: 'إعدادات الحساب', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#050505] flex flex-col md:flex-row transition-colors duration-500" dir="rtl">
      {/* Ambient Backdrop Main */}
      <div className="fixed inset-0 pointer-events-none bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.02] dark:opacity-5 mix-blend-overlay -z-10"></div>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm z-30 md:hidden transition-opacity duration-[400ms]"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:sticky top-0 right-0 z-40 w-72 h-screen backdrop-blur-xl bg-white/80 dark:bg-gradient-to-b dark:from-zinc-900/90 dark:to-zinc-900/90 border-l border-slate-200/50 dark:border-white/10 flex flex-col transition-transform duration-[400ms] ease-out shadow-[10px_0_50px_-15px_rgba(0,0,0,0.1)] dark:shadow-[10px_0_50px_-15px_rgba(0,0,0,0.5)] ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}`}>

        {/* Logo Section */}
        <div className="p-8 pb-6 hidden md:block relative">
          <Link to="/" className="relative text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-l from-primary-500 to-primary-600 group flex items-center gap-2 drop-shadow-md">
            <img src="/lobab.png" alt="Logo" className="w-20 h-auto" />

            <div className="absolute -inset-2 bg-primary-500/20 blur-xl rounded-full opacity-50 group-hover:opacity-80 animate-pulse transition-all duration-700 pointer-events-none"></div>
          </Link>
          <div className="mt-3 flex items-center gap-2">
            <span className="px-3 py-1 bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 text-xs font-extrabold rounded-full border border-primary-100 dark:border-primary-500/20">لوحة الطالب</span>
          </div>
        </div>

        {/* Student Quick Info */}

        {/* Navigation Section */}
        <div className="flex-1 overflow-y-auto py-6 px-5 no-scrollbar">
          <ul className="space-y-3">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              const Icon = item.icon;

              return (
                <li key={item.path} className="relative">
                  <Link
                    to={item.path}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`flex items-center gap-4 px-5 py-4 rounded-xl transition-all duration-[400ms] group overflow-hidden relative ${isActive
                      ? 'bg-gradient-to-l from-primary-500/10 to-transparent border-r-4 border-primary-500 shadow-[inset_-10px_0_20px_rgba(230,0,35,0.05)]'
                      : 'hover:bg-slate-100/60 dark:hover:bg-white/5 border-r-4 border-transparent hover:border-slate-300 dark:hover:border-white/10'
                      }`}
                  >
                    {/* Active State Background Cinematic Glow */}
                    {isActive && (
                      <div className="absolute inset-0 bg-primary-500/5 blur-[20px] scale-150 pointer-events-none"></div>
                    )}

                    <Icon className={`w-6 h-6 shrink-0 transition-transform duration-[400ms] ease-out group-hover:scale-110 relative z-10 ${isActive ? 'text-primary-500 drop-shadow-[0_0_12px_rgba(230,0,35,0.6)]' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-white'
                      }`} />

                    <span className={`text-[1.1rem] tracking-wide relative z-10 transition-colors duration-[400ms] ${isActive ? 'font-bold text-slate-900 dark:text-white drop-shadow-sm' : 'font-bold text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white'
                      }`}>
                      {item.label}
                    </span>

                    {item.badge > 0 && (
                      <span className="mr-auto bg-primary-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-primary-500/30 relative z-10">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Website Link */}
          <div className="mt-6 px-2 space-y-2">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/50 dark:border-white/5 text-slate-500 dark:text-zinc-400 hover:text-slate-800 dark:hover:text-white hover:border-slate-300 dark:hover:border-white/10 transition-all font-bold text-sm group"
            >
              <Globe className="w-4 h-4 group-hover:scale-110 transition-transform" />
              عرض صفحة الموقع
            </Link>
          </div>
        </div>

        {/* Bottom Actions Section */}
        <div className="p-7 border-t border-slate-200/50 dark:border-white/10 space-y-5 bg-slate-50/50 dark:bg-black/20">
          <div className="flex items-center justify-between px-2">
            <span className="text-[0.8rem] font-bold tracking-widest text-slate-500 dark:text-slate-400">إدارة الجلسات</span>
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-700/80 transition-all duration-[400ms] hover:scale-105 shadow-sm"
              title="تغيير المظهر"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5 text-primary-500 drop-shadow-sm" /> : <Moon className="w-5 h-5 text-slate-700" />}
            </button>
          </div>

          <button
            onClick={handleLogout}
            className="group w-full flex items-center gap-4 px-5 py-4 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-500 transition-all duration-[400ms] border border-transparent hover:border-rose-100 dark:hover:border-rose-500/20"
          >
            <LogOut className="w-6 h-6 transition-transform duration-[400ms] group-hover:-translate-x-1" />
            <span className="text-[1.1rem] font-bold tracking-wide">تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <DashboardTopBar setSidebarOpen={setIsSidebarOpen} />

        <main className="flex-1 w-full max-w-[100rem] mx-auto p-4 md:p-8 overflow-y-auto relative z-10 transition-colors duration-500">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
